import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Chip,
  useTheme,
  Stack
} from '@mui/material';
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Sparkles,
  Github,
} from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
export default function LoginPage() {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle login logic here
    console.log('Login attempt with:', formData);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(to bottom right, #fef2f2, #fdf2f8)',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Chip
            icon={<Sparkles className="w-4 h-4" />}
            label="Welcome Back"
            sx={{
              bgcolor: theme.palette.primary.main,
              color: 'white',
              fontSize: '1rem',
              py: 2,
              px: 2,
              mb: 2
            }}
          />
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Sign in to Bean
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Don't have an account?{' '}
            <Link to="/signup" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
              Sign up
            </Link>
          </Typography>
        </Box>

        {/* Login Form */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 4
          }}
        >
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail className="w-5 h-5" style={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock className="w-5 h-5" style={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ textAlign: 'right' }}>
                <Link
                  to="/forgot-password"
                  style={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    fontSize: '0.875rem'
                  }}
                >
                  Forgot password?
                </Link>
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  },
                  py: 1.5
                }}
                endIcon={<ArrowRight className="w-4 h-4" />}
              >
                Sign In
              </Button>
            </Stack>
          </form>

          <Divider sx={{ my: 4 }}>
            <Typography variant="body2" color="text.secondary">
              or continue with
            </Typography>
          </Divider>

          {/* Social Login Buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FcGoogle className="w-5 h-5" />}
              sx={{
                py: 1.5,
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
            >
              Google
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Github className="w-5 h-5" />}
              sx={{
                py: 1.5,
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover'
                }
              }}
            >
              GitHub
            </Button>
          </Stack>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            By signing in, you agree to our{' '}
            <Link
              to="/terms"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'none'
              }}
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link
              to="/privacy"
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'none'
              }}
            >
              Privacy Policy
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
